import React from 'react';
import {
  Container,
  Form,
  Button,
  Alert,
  Spinner,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import axios from 'axios';
import {useSearchParams, useNavigate} from 'react-router-dom';
import {FaEye, FaEyeSlash} from 'react-icons/fa';
import {getAxiosErrorMessage} from '../libs/helper';
import {isPasswordValid, specialCharsMessage} from './login';

/**
 * ResetPasswordPage
 *
 * Reads "token" from the query string and posts to /api/reset_password?token=...&new_password=...
 * Shows success / error UI and provides a link back to login.
 */
export default function ResetPasswordPage(): React.JSX.Element {
  const [searchParameters] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParameters.get('token') ?? '';

  const [password, setPassword] = React.useState<string>('');
  const [password2, setPassword2] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPassword2, setShowPassword2] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [seconds, setSeconds] = React.useState<number>(5);

  React.useEffect(() => {
    let interval: ReturnType<typeof globalThis.setInterval> | undefined;
    if (success && seconds > 0) {
      interval = globalThis.setInterval(() => {
        setSeconds(s => s - 1);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [success, seconds]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);

    if (!token) {
      setError('Missing token. Please use the link provided in your email.');
      return;
    }

    if (password !== password2) {
      setError('Passwords do not match');
      return;
    }

    if (!isPasswordValid(password)) {
      setError(
        'Password must be at least 12 characters and include one lowercase letter, one uppercase letter, one number, and one special character',
      );
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/reset_password', null, {
        params: {token, new_password: password}, // eslint-disable-line @typescript-eslint/naming-convention
      });
      setSuccess(true);
      // Auto-redirect to login after short countdown
      setSeconds(5);
      globalThis.setTimeout(() => {
        void navigate('/login');
      }, 5000);
    } catch (error: any) {
      const message = getAxiosErrorMessage(error) ?? 'Failed to reset password';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container className='my-5'>
      <h1>Reset Password</h1>

      {!token && (
        <Alert variant='warning'>
          No reset token provided. Please use the link in your email.
        </Alert>
      )}

      {error && <Alert variant='danger'>{error}</Alert>}

      {success ? (
        <Alert variant='success'>
          Password reset successfully. Redirecting to sign in in {seconds}{' '}
          second{seconds === 1 ? '' : 's'}.
          <div className='mt-3'>
            <Button
              variant='outline-primary'
              onClick={() => {
                void navigate('/login');
              }}
            >
              Go to sign in now
            </Button>
          </div>
        </Alert>
      ) : (
        <Form
          onSubmit={event => {
            void handleSubmit(event);
          }}
        >
          <Form.Group controlId='new-password' className='mb-3'>
            <Form.Label>New password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={event => {
                  setPassword(event.target.value);
                }}
                placeholder='New password'
                autoComplete='new-password'
                required
              />
              <OverlayTrigger
                placement='right'
                delay={{show: 250, hide: 400}}
                overlay={<Tooltip>{showPassword ? 'Hide' : 'Show'}</Tooltip>}
              >
                <Button
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                  variant='outline-secondary'
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </OverlayTrigger>
            </InputGroup>
          </Form.Group>

          <Form.Group controlId='confirm-password' className='mb-3'>
            <Form.Label>Confirm new password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword2 ? 'text' : 'password'}
                value={password2}
                onChange={event => {
                  setPassword2(event.target.value);
                }}
                placeholder='Confirm new password'
                autoComplete='new-password'
                required
              />
              <OverlayTrigger
                placement='right'
                delay={{show: 250, hide: 400}}
                overlay={<Tooltip>{showPassword2 ? 'Hide' : 'Show'}</Tooltip>}
              >
                <Button
                  onClick={() => {
                    setShowPassword2(!showPassword2);
                  }}
                  variant='outline-secondary'
                >
                  {showPassword2 ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </OverlayTrigger>
            </InputGroup>
          </Form.Group>

          <br />
          <Alert variant='info'>{specialCharsMessage}</Alert>

          <Button type='submit' disabled={loading || !token}>
            {loading ? (
              <>
                <Spinner animation='border' size='sm' className='me-2' />
                Resetting...
              </>
            ) : (
              'Reset password'
            )}
          </Button>
        </Form>
      )}
    </Container>
  );
}
